import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../config";
import placeimage from "../image/noimage.jpg";
import { Helmet } from "react-helmet-async";
import Loader from "../components/Loader";
import { enquiryAPI } from "../services/api";
import { toast } from "react-toastify";

export default function ServiceDetail() {
    const [service, setService] = useState({});
    const [trainer, setTrainer] = useState({});
    const [relatedServices, setRelatedServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [contactAction, setContactAction] = useState(''); // 'whatsapp', 'call', 'email'
    const [activeTab, setActiveTab] = useState('overview');

    // Booking Form State
    const [bookingData, setBookingData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const params = useParams();
    const navigate = useNavigate();

    // Check if user is logged in
    const isUserLoggedIn = () => {
        const user = localStorage.getItem('user');
        return !!user;
    };

    // Pre-fill user data if logged in
    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                setBookingData(prev => ({
                    ...prev,
                    name: user.name || '',
                    email: user.email || '',
                    phone: user.phone || ''
                }));
            } catch (e) {
                console.error("Error parsing user data", e);
            }
        }
    }, []);

    const fetchServiceDetail = useCallback(async () => {
        try {
            const response = await axios.get(
                `${config.apiUrl}/trainer/${params.trainerSlug}`
            );
            const trainerData = response.data.data;
            setTrainer(trainerData);

            const foundService = trainerData.services?.find(
                s => s.id === parseInt(params.serviceId) || s.slug === params.serviceId
            );

            if (!foundService) {
                navigate("/error");
                return;
            }
            setService(foundService);

            const related = trainerData.services?.filter(
                s => s.id !== foundService.id
            ).slice(0, 3) || [];
            setRelatedServices(related);
        } catch (error) {
            console.error("Error fetching service:", error);
            navigate("/error");
        } finally {
            setLoading(false);
        }
    }, [navigate, params.trainerSlug, params.serviceId]);

    useEffect(() => {
        fetchServiceDetail();
        window.scrollTo(0, 0);
    }, [fetchServiceDetail]);

    const imageOnError = (event) => {
        event.currentTarget.src = placeimage;
    };

    const handleContactAction = (action) => {
        setContactAction(action);
        setShowContactModal(true);
    };

    const executeContactAction = (action) => {
        switch (action) {
            case 'whatsapp':
                window.open(`https://wa.me/91${trainer.phone}?text=Hi, I'm interested in ${service.name}`, '_blank');
                break;
            case 'call':
                window.location.href = `tel:+91${trainer.phone}`;
                break;
            case 'email':
                window.location.href = `mailto:${trainer.email}?subject=Inquiry about ${service.name}`;
                break;
            default:
                break;
        }
    };

    const handleContactSubmit = (e) => {
        e.preventDefault();
        setShowContactModal(false);
        executeContactAction(contactAction);
    };

    const handleBookingClick = () => {
        if (isUserLoggedIn()) {
            setShowBookingModal(true);
        } else {
            navigate('/login', { state: { from: window.location.pathname } });
        }
    };

    const handleBookingChange = (e) => {
        const { name, value } = e.target;
        setBookingData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleBookingSubmit = async (e) => {
        e.preventDefault();

        if (!isUserLoggedIn()) {
            navigate('/login', { state: { from: window.location.pathname } });
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                trainer_id: trainer.id,
                service_id: service.id,
                service_name: service.name,
                name: bookingData.name,
                email: bookingData.email,
                phone: bookingData.phone,
                message: bookingData.message
            };

            const response = await enquiryAPI.create(payload);

            if (response.data.success) {
                toast.success("Consultation booked successfully! The trainer will contact you soon.");
                setShowBookingModal(false);
                setBookingData(prev => ({ ...prev, message: '' }));
            } else {
                toast.error(response.data.message || "Failed to book consultation.");
            }
        } catch (error) {
            console.error("Booking Error:", error);
            toast.error(error.response?.data?.message || "An error occurred. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const generateServiceUrl = () => {
        const serviceName = service.name?.toLowerCase().replace(/\s+/g, '-') || 'service';
        const trainerName = trainer.name?.toLowerCase().replace(/\s+/g, '-') || 'trainer';
        const city = trainer.city?.toLowerCase().replace(/\s+/g, '-') || 'india';
        return `/service/${trainerName}-${serviceName}-${city}`;
    };

    const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": service.name,
        "description": service.description || `${service.name} - Professional fitness training service by ${trainer.name}`,
        "provider": {
            "@type": "Person",
            "name": trainer.name,
            "image": trainer.photo,
            "telephone": trainer.phone,
            "email": trainer.email,
            "address": {
                "@type": "PostalAddress",
                "addressLocality": trainer.city,
                "addressCountry": "IN"
            }
        },
        "areaServed": {
            "@type": "City",
            "name": trainer.city
        },
        "offers": {
            "@type": "Offer",
            "price": service.price?.replace(/[^\d]/g, ''),
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock"
        },
        "serviceType": service.type || "Fitness Training"
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <Helmet>
                <title>{service.name} by {trainer.name} in {trainer.city} | Expert Fitness Training</title>
                <meta name="description" content={`${service.name} - ${service.description || `Professional fitness training service by certified trainer ${trainer.name} in ${trainer.city}. Starting at ${service.price}. Book your session today!`}`} />
                <meta name="keywords" content={`${service.name}, ${trainer.name}, fitness training ${trainer.city}, personal trainer, ${service.type} training, fitness coach, gym trainer ${trainer.city}, workout program, fitness service`} />
                <meta property="og:title" content={`${service.name} by ${trainer.name}`} />
                <meta property="og:description" content={`Professional ${service.name} service in ${trainer.city}. Book now at ${service.price}`} />
                <meta property="og:image" content={trainer.photo} />
                <meta property="og:url" content={`${window.location.origin}${generateServiceUrl()}`} />
                <meta property="og:type" content="product" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${service.name} - ${trainer.name}`} />
                <meta name="twitter:description" content={`Expert fitness training in ${trainer.city}`} />
                <meta name="twitter:image" content={trainer.photo} />
                <link rel="canonical" href={`${window.location.origin}${generateServiceUrl()}`} />
                <script type="application/ld+json">
                    {JSON.stringify(serviceSchema)}
                </script>
            </Helmet>

            <div className="service-detail-page">
                <section className="breadcrumb-section">
                    <div className="container">
                        <nav className="breadcrumb-nav">
                            <Link to="/">Home</Link>
                            <i className="bi bi-chevron-right"></i>
                            <Link to="/gym-fitness-trainer">Trainers</Link>
                            <i className="bi bi-chevron-right"></i>
                            <Link to={`/gym-fitness-trainer/${trainer.city?.toLowerCase()}`}>{trainer.city}</Link>
                            <i className="bi bi-chevron-right"></i>
                            <Link to={`/gym-fitness-trainer/${trainer.city?.toLowerCase()}/${params.trainerSlug}`}>{trainer.name}</Link>
                            <i className="bi bi-chevron-right"></i>
                            <span>{service.name}</span>
                        </nav>
                    </div>
                </section>

                <section className="service-hero-section">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <div className="service-hero-content">
                                    <div className="service-type-badge-wrapper">
                                        <span className={`service-type-badge ${service.type?.toLowerCase()}`}>
                                            <i className={`bi ${service.type === 'Online' ? 'bi-laptop' : service.type === 'Offline' ? 'bi-geo-alt' : 'bi-globe'}`}></i>
                                            {service.type || 'Offline'} Training
                                        </span>
                                        <span className="verified-service-badge">
                                            <i className="bi bi-patch-check-fill"></i>
                                            Verified Service
                                        </span>
                                    </div>
                                    <h1 className="service-main-title">{service.name}</h1>
                                    <div className="service-trainer-info">
                                        <img src={trainer.photo} alt={trainer.name} onError={imageOnError} className="trainer-avatar-small" />
                                        <div>
                                            <p className="trainer-label">Offered by</p>
                                            <Link to={`/gym-fitness-trainer/${trainer.city?.toLowerCase()}/${params.trainerSlug}`} className="trainer-name-link">{trainer.name}</Link>
                                            <div className="trainer-location-small">
                                                <i className="bi bi-geo-alt-fill"></i>
                                                {trainer.city}, India
                                            </div>
                                        </div>
                                    </div>
                                    <div className="service-stats-row">
                                        <div className="stat-item">
                                            <i className="bi bi-people-fill stat-icon"></i>
                                            <span className="stat-value">200+</span>
                                            <span className="stat-label">Clients Trained</span>
                                        </div>
                                        <div className="stat-divider"></div>
                                        <div className="stat-item">
                                            <i className="bi bi-clock-fill stat-icon"></i>
                                            <span className="stat-value">{service.duration || service.durationUnit}</span>
                                            <span className="stat-label">Duration</span>
                                        </div>
                                    </div>
                                    <div className="service-price-cta-section">
                                        {/* <div className="service-price-box">
                                            <div className="price-label">Starting at</div>
                                            <div className="price-amount-large">{service.price}</div>
                                            <div className="price-note">*Flexible payment options available</div>
                                        </div> */}
                                        <div className="cta-buttons-group-four">
                                            <button className="btn-consultation-hero" onClick={handleBookingClick}>
                                                <i className="bi bi-calendar-check-fill"></i>
                                                Book Free Consultation
                                            </button>
                                            <button className="btn-whatsapp-hero" onClick={() => handleContactAction('whatsapp')}>
                                                <i className="bi bi-whatsapp"></i>
                                                WhatsApp
                                            </button>
                                            <button className="btn-call-hero" onClick={() => handleContactAction('call')}>
                                                <i className="bi bi-telephone-fill"></i>
                                                Call
                                            </button>
                                            <button className="btn-email-hero" onClick={() => handleContactAction('email')}>
                                                <i className="bi bi-envelope-fill"></i>
                                                Email
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="service-hero-image-wrapper">
                                    <img src={trainer.photo} alt={`${service.name} by ${trainer.name}`} onError={imageOnError} className="service-hero-image" />
                                    <div className="service-image-overlay">
                                        <div className="overlay-badge">
                                            <i className="bi bi-award-fill"></i>
                                            <span>Certified Professional</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="service-details-section">
                    <div className="container">
                        <div className="row g-4">
                            <div className="col-lg-8">
                                <div className="service-tabs-nav">
                                    <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                                        <i className="bi bi-info-circle"></i> Overview
                                    </button>
                                    <button className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`} onClick={() => setActiveTab('features')}>
                                        <i className="bi bi-check-circle"></i> What's Included
                                    </button>
                                    <button className={`tab-btn ${activeTab === 'benefits' ? 'active' : ''}`} onClick={() => setActiveTab('benefits')}>
                                        <i className="bi bi-trophy"></i> Benefits
                                    </button>
                                    <button className={`tab-btn ${activeTab === 'faq' ? 'active' : ''}`} onClick={() => setActiveTab('faq')}>
                                        <i className="bi bi-question-circle"></i> FAQ
                                    </button>
                                </div>

                                <div className="service-tab-content">
                                    {activeTab === 'overview' && (
                                        <div className="tab-pane-content">
                                            <h2 className="content-heading">About This Service</h2>
                                            <div className="service-description">
                                                <p>{service.description || `${service.name} is a comprehensive fitness training program designed to help you achieve your health and fitness goals. Led by certified trainer ${trainer.name} with ${trainer.experience}+ years of experience, this service combines expert guidance, personalized workout plans, and continuous support to ensure your success.`}</p>
                                                <p>Whether you're looking to lose weight, build muscle, improve endurance, or simply maintain a healthy lifestyle, this {service.type?.toLowerCase()} training program is tailored to meet your specific needs and fitness level.</p>
                                            </div>
                                            <h3 className="sub-heading">Who Is This For?</h3>
                                            <div className="ideal-for-grid">
                                                <div className="ideal-for-card">
                                                    <i className="bi bi-person-check-fill"></i>
                                                    <h4>Beginners</h4>
                                                    <p>New to fitness and need guidance</p>
                                                </div>
                                                <div className="ideal-for-card">
                                                    <i className="bi bi-graph-up-arrow"></i>
                                                    <h4>Intermediate</h4>
                                                    <p>Looking to level up your training</p>
                                                </div>
                                                <div className="ideal-for-card">
                                                    <i className="bi bi-trophy-fill"></i>
                                                    <h4>Advanced</h4>
                                                    <p>Want specialized coaching</p>
                                                </div>
                                                <div className="ideal-for-card">
                                                    <i className="bi bi-heart-pulse-fill"></i>
                                                    <h4>Health Focus</h4>
                                                    <p>Prioritizing overall wellness</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'features' && (
                                        <div className="tab-pane-content">
                                            <h2 className="content-heading">What's Included in This Service</h2>
                                            <div className="features-grid">
                                                {service.features && service.features.length > 0 ? (
                                                    service.features.map((feature, idx) => (
                                                        <div key={idx} className="feature-item-card">
                                                            <i className="bi bi-check-circle-fill feature-icon"></i>
                                                            <div className="feature-text">{feature}</div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <>
                                                        <div className="feature-item-card"><i className="bi bi-check-circle-fill feature-icon"></i><div className="feature-text">Personalized workout plans tailored to your goals</div></div>
                                                        <div className="feature-item-card"><i className="bi bi-check-circle-fill feature-icon"></i><div className="feature-text">One-on-one training sessions with certified trainer</div></div>
                                                        <div className="feature-item-card"><i className="bi bi-check-circle-fill feature-icon"></i><div className="feature-text">Nutrition guidance and meal planning support</div></div>
                                                        <div className="feature-item-card"><i className="bi bi-check-circle-fill feature-icon"></i><div className="feature-text">Progress tracking and regular assessments</div></div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'benefits' && (
                                        <div className="tab-pane-content">
                                            <h2 className="content-heading">Expected Benefits & Results</h2>
                                            <div className="benefits-grid">
                                                <div className="benefit-card">
                                                    <div className="benefit-icon-wrapper"><i className="bi bi-heart-pulse-fill"></i></div>
                                                    <h4>Improved Health</h4>
                                                    <p>Better cardiovascular health, increased energy levels, and enhanced overall wellness.</p>
                                                </div>
                                                <div className="benefit-card">
                                                    <div className="benefit-icon-wrapper"><i className="bi bi-lightning-charge-fill"></i></div>
                                                    <h4>Increased Strength</h4>
                                                    <p>Build lean muscle mass and improve functional strength for daily activities.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'faq' && (
                                        <div className="tab-pane-content">
                                            <h2 className="content-heading">Frequently Asked Questions</h2>
                                            <div className="faq-accordion">
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <i className="bi bi-question-circle-fill"></i>
                                                        <h4>How do I get started with this service?</h4>
                                                    </div>
                                                    <div className="faq-answer">
                                                        <p>Simply click the "Book This Service" button above or contact {trainer.name} via WhatsApp. You'll schedule a free initial consultation to discuss your goals and create a customized plan.</p>
                                                    </div>
                                                </div>
                                                <div className="faq-item">
                                                    <div className="faq-question">
                                                        <i className="bi bi-question-circle-fill"></i>
                                                        <h4>What equipment do I need?</h4>
                                                    </div>
                                                    <div className="faq-answer">
                                                        <p>Equipment requirements vary based on the training type. For {service.type?.toLowerCase()} training, {trainer.name} will provide guidance on necessary equipment during your consultation.</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="trainer-about-section">
                                    <h2 className="content-heading">About Your Trainer</h2>
                                    <div className="trainer-about-card">
                                        <div className="trainer-about-header">
                                            <img src={trainer.photo} alt={trainer.name} onError={imageOnError} className="trainer-about-photo" />
                                            <div className="trainer-about-info">
                                                <h3>{trainer.name}</h3>
                                                <p className="trainer-title">Certified Personal Trainer</p>
                                                <div className="trainer-experience-badge">
                                                    <i className="bi bi-award-fill"></i>
                                                    {trainer.experience}+ Years Experience
                                                </div>
                                            </div>
                                        </div>
                                        <div className="trainer-about-content" dangerouslySetInnerHTML={{ __html: trainer.about?.substring(0, 500) + '...' || `${trainer.name} is a certified fitness professional dedicated to helping clients achieve their health and fitness goals.` }} />
                                        <Link to={`/gym-fitness-trainer/${trainer.city?.toLowerCase()}/${params.trainerSlug}`} className="btn-view-full-profile">
                                            View Full Profile <i className="bi bi-arrow-right"></i>
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-4">
                                <div className="service-sidebar-card sticky-sidebar">
                                    {/* <div className="sidebar-price-section">
                                        <div className="sidebar-price-label">Service Price</div>
                                        <div className="sidebar-price-amount">{service.price}</div>
                                        <div className="sidebar-price-note">
                                            <i className="bi bi-info-circle"></i> Flexible payment plans available
                                        </div>
                                    </div> */}
                                    <div className="sidebar-service-meta">
                                        <div className="meta-row"><i className="bi bi-clock-fill"></i><span>Duration: {service.duration || service.durationUnit}</span></div>
                                        <div className="meta-row"><i className="bi bi-geo-alt-fill"></i><span>Location: {trainer.city}</span></div>
                                        <div className="meta-row"><i className="bi bi-laptop"></i><span>Type: {service.type || 'Offline'}</span></div>
                                    </div>
                                    <button className="btn-consultation-sidebar" onClick={handleBookingClick}>
                                        <i className="bi bi-calendar-check-fill"></i> Book Free Consultation
                                    </button>
                                    <button className="btn-whatsapp-sidebar" onClick={() => handleContactAction('whatsapp')}>
                                        <i className="bi bi-whatsapp"></i> WhatsApp
                                    </button>
                                    <button className="btn-call-sidebar" onClick={() => handleContactAction('call')}>
                                        <i className="bi bi-telephone-fill"></i> Call Now
                                    </button>
                                    <button className="btn-email-sidebar" onClick={() => handleContactAction('email')}>
                                        <i className="bi bi-envelope-fill"></i> Send Email
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="service-cta-section">
                    <div className="container">
                        <div className="cta-content-wrapper">
                            <div className="cta-text-content">
                                <h2>Ready to Transform Your Fitness Journey?</h2>
                                <p>Book your free consultation with {trainer.name} today and take the first step towards your fitness goals!</p>
                            </div>
                            <div className="cta-buttons-wrapper">
                                <button className="btn-cta-primary" onClick={handleBookingClick}><i className="bi bi-calendar-check-fill"></i> Book Free Consultation</button>
                                <button className="btn-cta-secondary" onClick={() => handleContactAction('whatsapp')}><i className="bi bi-whatsapp"></i> WhatsApp</button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Booking Modal */}
            {showBookingModal && (
                <div className="modal-overlay-custom" onClick={() => setShowBookingModal(false)}>
                    <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-custom">
                            <h3>Book {service.name}</h3>
                            <button className="modal-close-btn" onClick={() => setShowBookingModal(false)}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="modal-body-custom">
                            <p>Fill in your details and we'll get back to you within 24 hours!</p>
                            <form className="booking-form" onSubmit={handleBookingSubmit}>
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Your Name"
                                    className="form-input-custom"
                                    value={bookingData.name}
                                    onChange={handleBookingChange}
                                    required
                                />
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Your Email"
                                    className="form-input-custom"
                                    value={bookingData.email}
                                    onChange={handleBookingChange}
                                    required
                                />
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Your Phone"
                                    className="form-input-custom"
                                    value={bookingData.phone}
                                    onChange={handleBookingChange}
                                    required
                                />
                                <input
                                    type="text"
                                    value={service.name}
                                    className="form-input-custom"
                                    disabled
                                />
                                <textarea
                                    name="message"
                                    placeholder="Your Message (Optional)"
                                    className="form-textarea-custom"
                                    rows="3"
                                    value={bookingData.message}
                                    onChange={handleBookingChange}
                                ></textarea>
                                <button type="submit" className="btn-submit-booking" disabled={submitting}>
                                    <i className={`bi ${submitting ? 'bi-hourglass-split' : 'bi-send-fill'}`}></i>
                                    {submitting ? 'Submitting...' : 'Submit Booking Request'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Modal */}
            {showContactModal && (
                <div className="modal-overlay-custom" onClick={() => setShowContactModal(false)}>
                    <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-custom">
                            <h3>
                                {contactAction === 'whatsapp' && 'WhatsApp Contact'}
                                {contactAction === 'call' && 'Call Request'}
                                {contactAction === 'email' && 'Email Inquiry'}
                            </h3>
                            <button className="modal-close-btn" onClick={() => setShowContactModal(false)}>
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                        <div className="modal-body-custom">
                            <p>
                                {contactAction === 'whatsapp' && 'Please provide your details to connect via WhatsApp'}
                                {contactAction === 'call' && 'Please provide your details and we\'ll call you back'}
                                {contactAction === 'email' && 'Please provide your details to send an email inquiry'}
                            </p>
                            <form className="booking-form" onSubmit={handleContactSubmit}>
                                <input type="text" name="name" placeholder="Your Name" className="form-input-custom" required />
                                <input type="tel" name="phone" placeholder="Your Phone" className="form-input-custom" required />
                                <input type="email" name="email" placeholder="Your Email" className="form-input-custom" required />
                                <button type="submit" className="btn-submit-booking">
                                    <i className="bi bi-check-circle-fill"></i>
                                    {contactAction === 'whatsapp' && 'Continue to WhatsApp'}
                                    {contactAction === 'call' && 'Request Call'}
                                    {contactAction === 'email' && 'Send Email'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
